import React, { useState, useEffect } from 'react';
import EvidenceCard, { getClassificationOption } from './EvidenceCard';
import { getConsistencyGuardNotesApi, saveConsistencyGuardNoteApi, getConsistencyGuardDetailsApi, updateConsistencyGuardStatusApi, archiveInconsistencyApi } from '../../../services/AxiosServices';
import { useAuth0 } from '@auth0/auth0-react';

interface Note {
  user: string;
  avatar: string | React.ReactNode;
  text: string;
  datetime: string;
}

interface RightPanelProps {
  selectedItem: any;
  reviewers?: string[];
  onNext?: () => void;
  onPrev?: () => void;
  onRefreshData?: () => void;
  onReset?: () => void;
  onArchive?: () => void;
}

const RightPanel: React.FC<RightPanelProps> = ({ selectedItem, onNext, onPrev, onRefreshData, onReset, onArchive }) => {
  const [activeTab, setActiveTab] = useState<'inconsistency' | 'notes'>('inconsistency');
  const [noteInput, setNoteInput] = useState('');
  const [notesList, setNotesList] = useState<Note[]>([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [detailsData, setDetailsData] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [localLabels, setLocalLabels] = useState<Record<string, string>>({});

  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    setLocalLabels({});
  }, [selectedItem, detailsData]);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!selectedItem) {
        setDetailsData(null);
        return;
      }

      const ruleId = selectedItem.rule_id || selectedItem.ruleId || selectedItem.id;
      const inconsistencyId = selectedItem.inconsistency_id || selectedItem.inconsistencyId;

      if (!ruleId || !inconsistencyId) return;

      setLoadingDetails(true);
      try {
        const token = await getAccessTokenSilently();
        const res: any = await getConsistencyGuardDetailsApi(ruleId, inconsistencyId, token);
        if (res) {
          setDetailsData(res);
        }
      } catch (err) {
        console.error("Error fetching inconsistency details:", err);
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchDetails();
  }, [selectedItem, getAccessTokenSilently]);

  const fetchNotes = async () => {
    const inconsistencyId = selectedItem?.inconsistency_id || selectedItem?.inconsistencyId || selectedItem?.id;
    if (!inconsistencyId) return;
    try {
      const token = await getAccessTokenSilently();
      const data: any = await getConsistencyGuardNotesApi(inconsistencyId, token);

      if (data) {
        const notesArray = Array.isArray(data) ? data : (data.notes || data.detail || []);
        const formattedNotes = notesArray.map((note: any) => {
          let dateStr = note.created_on || note.createdDate || note.createdAt || note.datetime || note.timestamp || '';
          if (dateStr) {
            const dateObj = new Date(dateStr);
            if (!isNaN(dateObj.getTime())) {
              dateStr = dateObj.toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
            }
          }

          let noteUser = 'Unknown';
          let avatarUrl = null;

          if (note.user && typeof note.user === 'object') {
            noteUser = note.user.name || note.user.nickname || note.user.username || note.user.preferred_username || note.user.email || 'Unknown';
            avatarUrl = note.user.picture || note.user.avatar || note.user.avatar_url;
          } else {
            noteUser = note.created_by_name || note.name || note.nickname || note.userName || note.user_name || note.username || note.user || note.author || note.created_by || note.email || 'Unknown';
          }

          if (!avatarUrl) {
            avatarUrl = note.creator_avatar_link || note.picture || note.avatar_url || note.avatarUrl || note.avatar || note.profile_image || note.userAvatar;
          }

          let avatarElement: any = 'U';
          if (avatarUrl) {
            avatarElement = <img src={avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />;
          } else if (noteUser && noteUser !== 'Unknown') {
            avatarElement = noteUser.charAt(0).toUpperCase();
          }

          return {
            user: noteUser,
            avatar: avatarElement,
            text: note.note_content || note.text || note.comment || note.content || note.noteText || '',
            datetime: dateStr
          };
        });
        setNotesList(formattedNotes);
      }
    } catch (error) {
      console.error("Failed to fetch notes", error);
    }
  };

  useEffect(() => {
    if (activeTab === 'notes') {
      fetchNotes();
    }
  }, [activeTab, selectedItem]);

  useEffect(() => {
    setNoteInput('');
  }, [selectedItem]);

  const handleSaveNote = () => {
    if (noteInput.trim()) {
      setIsConfirmOpen(true);
    }
  };

  const confirmSaveNote = async () => {
    const inconsistencyId = selectedItem?.inconsistency_id || selectedItem?.inconsistencyId || selectedItem?.id;
    if (!inconsistencyId || !noteInput.trim()) return;

    try {
      const token = await getAccessTokenSilently();
      const response = await saveConsistencyGuardNoteApi(inconsistencyId, { note_content: noteInput }, token);

      if (response) {
        setIsConfirmOpen(false);
        setNoteInput('');
        fetchNotes();
      } else {
        console.error("Failed to save note");
      }
    } catch (error) {
      console.error("Error saving note", error);
    }
  };

  if (!selectedItem) {
    return (
      <div className="cg-right-panel" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#94a3b8' }}>Select an inconsistency to view details</p>
      </div>
    );
  }

  const inconsistencyData = Array.isArray(detailsData?.inconsistency)
    ? detailsData.inconsistency[0]
    : (detailsData?.inconsistency && typeof detailsData.inconsistency === 'object' ? detailsData.inconsistency : null);

  const itemData = inconsistencyData || detailsData?.details?.[0] || (detailsData && !Array.isArray(detailsData) ? detailsData : {}) || {};

  const displayTitle = itemData?.inconsistency_title || itemData?.title || selectedItem?.title || '';

  // const currentReviewer = typeof itemData?.reviewer === 'object'
  //   ? (itemData?.reviewer?.user_name || itemData?.reviewer?.name || selectedItem.reviewer)
  //   : (itemData?.reviewer || selectedItem.reviewer || '');

  const ruleObj = Array.isArray(detailsData?.rule)
    ? detailsData.rule[0]
    : (detailsData?.rule || itemData?.rule);

  const ruleTitle = ruleObj?.description || ruleObj?.title || detailsData?.rule_title || itemData?.rule_title || ruleObj?.rule_title || ruleObj?.name || '-';

  const extractRuleSource = (rule: any) => {
    if (!rule) return detailsData?.rule_source || itemData?.rule_source || '-';
    const src = rule.source;
    if (typeof src === 'string' && src.trim()) return src;
    if (Array.isArray(src) && src.length > 0) {
      const first = src[0];
      if (typeof first === 'string' && first.trim()) return first;
      return first?.file_name || first?.filename || first?.fileName || first?.name || '-';
    }
    if (src && typeof src === 'object') {
      return src.file_name || src.filename || src.fileName || src.name || '-';
    }
    return rule.file_name || rule.filename || rule.fileName || detailsData?.rule_source || itemData?.rule_source || '-';
  };

  const ruleSource = extractRuleSource(ruleObj);

  const currentInconsistencyId = itemData.inconsistency_id || itemData.id || selectedItem?.inconsistency_id || selectedItem?.inconsistencyId || selectedItem?.id;

  let rawEvidenceList: any[] = [];
  if (Array.isArray(detailsData?.inconsistency) && detailsData.inconsistency.length > 0) {
    rawEvidenceList = detailsData.inconsistency;
  } else if (detailsData?.inconsistency && typeof detailsData.inconsistency === 'object' && Object.keys(detailsData.inconsistency).length > 0) {
    rawEvidenceList = [detailsData.inconsistency];
  } else if (Array.isArray(detailsData?.details) && detailsData.details.length > 0) {
    rawEvidenceList = detailsData.details;
  } else if (Array.isArray(detailsData) && detailsData.length > 0) {
    rawEvidenceList = detailsData;
  } else if (detailsData && typeof detailsData === 'object') {
    const explicitArray = detailsData.evidence || detailsData.evidences || detailsData.evidence_list || detailsData.passages || detailsData.conflicted_passages || detailsData.instances;
    if (Array.isArray(explicitArray) && explicitArray.length > 0) {
      rawEvidenceList = explicitArray;
    } else if (detailsData.conflicted_text || detailsData.text || detailsData.snippet || detailsData.title || detailsData.inconsistency_id) {
      rawEvidenceList = [detailsData];
    } else if (explicitArray && typeof explicitArray === 'object') {
      rawEvidenceList = [explicitArray];
    }
  }

  const LABEL_OPTIONS = ['Approved version', 'To be corrected', 'Intentional difference', 'Literature', 'Not relevant'];

  function normalizeLabel(label: string): string {
    if (!label || typeof label !== 'string') return 'Choose appropriate';
    if (label === 'NOT_SELECTED' || !label.trim()) return 'Choose appropriate';
    const normalized = label.toLowerCase().replace(/[\s_]+/g, '');
    for (const option of LABEL_OPTIONS) {
      if (option.toLowerCase().replace(/[\s_]+/g, '') === normalized) {
        return option;
      }
    }
    return label;
  }

  const mappedEvidence = rawEvidenceList.map((ev: any, index: number) => {

    let fallbackLabel = itemData?.businessClassification || itemData?.business_classification 
      || ev?.businessClassification || ev?.business_classification 
      || ev.label || ev.status || ev.classification || 'Choose appropriate';

    fallbackLabel = normalizeLabel(fallbackLabel as string);

    return {
      id: itemData.inconsistency_id || `ev-${index}`,
      title: itemData.snippet || displayTitle || 'Inconsistency',
      file_name: itemData.file_name || 'Document',
      snippet: ev.snippet || itemData.snippet || '',
      conflicting_text: ev.conflicting_text || itemData.conflicting_text,
      page: itemData.page_number || itemData.page || itemData.page_num || '',
      label: fallbackLabel,
      downloadable_url: itemData.downloadable_url || null
    };
  });

  const renderEvidenceList = mappedEvidence;

  const handleConfirm = async () => {
    try {
      const inconsistencyId = currentInconsistencyId || selectedItem?.inconsistency_id || selectedItem?.id;
      if (!inconsistencyId) {
        console.error("No inconsistency_id found to confirm");
        return;
      }

      const token = await getAccessTokenSilently();
      const targetList = mappedEvidence.length > 0 ? mappedEvidence : [{ id: inconsistencyId, label: 'APPROVED_VERSION' }];

      const updatePromises = targetList.map((ev: any) => {
        const incId = ev.id || inconsistencyId;
        const currentVal = localLabels[incId] || localLabels[ev.id] || ev.label || 'APPROVED_VERSION';
        const opt = getClassificationOption(currentVal);
        const classificationCode = opt ? opt.code : 'APPROVED_VERSION';
        const payload = {
          business_classification: classificationCode
        };
        return updateConsistencyGuardStatusApi(incId, payload, token);
      });

      const results = await Promise.all(updatePromises);
      const isSuccess = results.every(res => res !== null);

      if (isSuccess) {
        if (onRefreshData) {
          onRefreshData();
        }

        const params = new URLSearchParams(window.location.search);
        if (params.has('code') || params.has('state')) {
          params.delete('code');
          params.delete('state');
          const qs = params.toString();
          window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleResetAction = async () => {
    try {
      const ruleId = selectedItem.rule_id || selectedItem.ruleId || selectedItem.id;
      const inconsistencyId = selectedItem.inconsistency_id || selectedItem.inconsistencyId || currentInconsistencyId;
      const token = await getAccessTokenSilently();

      const targetList = mappedEvidence.length > 0 ? mappedEvidence : [{ id: inconsistencyId }];
      const updatePromises = targetList.map((ev: any) => {
        const incId = ev.id || inconsistencyId;
        return updateConsistencyGuardStatusApi(incId, { business_classification: 'NOT_SELECTED' }, token);
      });
      await Promise.all(updatePromises);

      setLoadingDetails(true);
      if (ruleId && inconsistencyId) {
        try {
          const res: any = await getConsistencyGuardDetailsApi(ruleId, inconsistencyId, token);
          if (res) {
            setDetailsData(res);
          }
        } catch (err) {
          console.error("Error fetching inconsistency details after reset:", err);
        }
      }
      setLoadingDetails(false);

      if (onReset) {
        onReset();
      }
    } catch (error) {
      console.error("Error updating status for reset:", error);
    }
  };

  const handleArchiveAction = async () => {
    try {
      const ruleId = selectedItem.rule_id || selectedItem.ruleId || selectedItem.id;
      const inconsistencyId = selectedItem.inconsistency_id || selectedItem.inconsistencyId || currentInconsistencyId;

      const token = await getAccessTokenSilently();
      const response = await archiveInconsistencyApi(inconsistencyId, token);

      if (response) {
        setLoadingDetails(true);
        if (ruleId && inconsistencyId) {
          try {
            const res: any = await getConsistencyGuardDetailsApi(ruleId, inconsistencyId, token);
            if (res) {
              setDetailsData(res);
            }
          } catch (err) {
            console.error("Error fetching inconsistency details after archive:", err);
          }
        }
        setLoadingDetails(false);

        if (onArchive) {
          onArchive();
        }
      }
    } catch (error) {
      console.error("Error updating status for archive:", error);
    }
  };

  return (
    <div className="cg-right-panel">
      <div className="cg-details-header">
        <div className="cg-details-title-row">
          <h3 className="cg-details-title"><span style={{ fontWeight: 400 }}>Title:</span> {displayTitle}</h3>
        </div>
      </div>

      <div className="cg-right-panel-scrollable">
        <div style={{ padding: '0 30px' }}>
          <table className="cg-meta-table">
            <tbody>
              <tr>
                <td className="cg-meta-label">Category</td>
                <td className="cg-meta-value">{selectedItem.categoryDetail || selectedItem.category}</td>
              </tr>
              <tr>
                <td className="cg-meta-label">Reasoning</td>
                <td className="cg-meta-value">{itemData?.reason || selectedItem.reason || "-"}</td>
              </tr>
              <tr>
                <td className="cg-meta-label">Rule</td>
                <td className="cg-meta-value">{ruleTitle}</td>
              </tr>
              <tr>
                <td className="cg-meta-label">Rule source</td>
                <td className="cg-meta-value">{ruleSource}</td>
              </tr>
              <tr>
                <td className="cg-meta-label">First detected</td>
                <td className="cg-meta-value">{selectedItem.firstDetected || "-"}</td>
              </tr>
              <tr>
                <td className="cg-meta-label">Last change</td>
                <td className="cg-meta-value">{selectedItem.lastChange || "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="cg-tabs">
          <button
            className={`cg-tab ${activeTab === 'inconsistency' ? 'active' : ''}`}
            onClick={() => setActiveTab('inconsistency')}
          >
            Inconsistency
          </button>
          <button
            className={`cg-tab ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            Notes
          </button>
        </div>

        <div className="cg-tab-content">
          {activeTab === 'inconsistency' ? (
            <div className="cg-evidence-grid">
              {loadingDetails && (
                <div style={{ color: '#64748b', fontSize: '13px', width: '100%', gridColumn: '1 / -1' }}>
                  Loading inconsistency details...
                </div>
              )}
              {!loadingDetails && renderEvidenceList && renderEvidenceList.length > 0 ? (
                renderEvidenceList.map((ev: any) => (
                  <EvidenceCard
                    key={ev.id}
                    evidence={ev}
                    onLabelChange={(label) => setLocalLabels(prev => ({ ...prev, [ev.id]: label }))}
                  />
                ))
              ) : (
                !loadingDetails && <p style={{ color: '#64748b' }}>No evidence available for this inconsistency.</p>
              )}
            </div>
          ) : (
            <div className="cg-notes-container">
              <div className="cg-note-input-container">
                <textarea
                  className="cg-note-input"
                  placeholder="Add comment or rationale"
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                />
                <button
                  className="cg-note-save-btn"
                  onClick={handleSaveNote}
                  disabled={!noteInput.trim()}
                >
                  Save note
                </button>
              </div>

              <div className="cg-notes-list">
                {notesList.length > 0 ? (
                  notesList.map((note, index) => (
                    <div className="cg-note-item" key={index}>
                      <div className="cg-note-avatar">{note.avatar}</div>
                      <div className="cg-note-content">
                        <div className="cg-note-header">
                          <span className="cg-note-user">{note.user}</span>
                          <span className="cg-note-time">{note.datetime}</span>
                        </div>
                        <p className="cg-note-text">{note.text}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p style={{ color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', marginTop: '20px' }}>
                    No notes provided.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="cg-actions-footer">
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
          <span className="cg-footer-nav" onClick={onPrev}>Previous</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {(!itemData?.business_classification || itemData.business_classification === 'NOT_SELECTED') ? (
            <button
              className="cg-btn-primary"
              onClick={handleConfirm}
              disabled={
                loadingDetails ||
                mappedEvidence.length === 0 ||
                !mappedEvidence.every((ev: any) => {
                  const currentCode = localLabels[ev.id] || ev.label;
                  return getClassificationOption(currentCode) !== null;
                })
              }
            >Confirm</button>
          ) : (
            activeTab === 'inconsistency' && (
              <div style={{ display: 'flex', gap: '12px' }}>
                <button className="cg-btn-secondary" onClick={handleResetAction}>Reset</button>
                <button className="cg-btn-primary" onClick={handleArchiveAction}>Archive</button>
              </div>
            )
          )}
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <span className="cg-footer-nav" onClick={onNext}>Next</span>
        </div>
      </div>

      {isConfirmOpen && (
        <div className="cg-modal-overlay">
          <div className="cg-modal-content">
            <p className="cg-modal-text">Do you want to save the note?</p>
            <div className="cg-modal-actions">
              <button
                className="cg-modal-cancel-btn"
                onClick={() => setIsConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                className="cg-modal-confirm-btn"
                onClick={confirmSaveNote}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RightPanel;
